'use client'
import { StoryblokComponent } from '@storyblok/react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { type TextContentBlockProps } from '@/blocks/TextContentBlock'
import { ButtonNextLink } from '@/components/ButtonNextLink'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder/ImageWithPlaceholder'
import { useProductData } from '@/components/ProductData/ProductDataProvider'
import { AddonComparisonDialogBody } from '@/features/addonComparison/AddonComparisonDialogBody'
import type { SbBaseBlockProps, ExpectedBlockType, StoryblokAsset } from '@/services/storyblok/storyblok'
import { card, imageContainer } from './AddonBlock.css'

type AddonBlockProps = SbBaseBlockProps<{
  image: StoryblokAsset
  content: ExpectedBlockType<TextContentBlockProps>
}>

export function AddonBlock({ blok, nested }: AddonBlockProps) {
  const { t } = useTranslation()
  const productData = useProductData()

  const baseTermsVersion = productData.variants.at(-1)?.termsVersion

  return (
    <GridLayout.Root nested={nested}>
      <GridLayout.Content width="1" align="center">
        <Card.Root className={card}>
          <div className={imageContainer}>
            <ImageWithPlaceholder src={blok.image.filename} alt={blok.image.alt} fill={true} />
          </div>

          <section className={yStack({ gap: 'sm', justifyContent: 'space-between' })}>
            {blok.content.map((nestedBlok) => (
              <StoryblokComponent key={nestedBlok._uid} blok={nestedBlok} nested={true} />
            ))}

            <footer className={xStack({ gap: 'xs' })}>
              <ButtonNextLink href={productData.priceCalculatorPageLink} variant="primary">
                {t('GET_PRICE_LINK')}
              </ButtonNextLink>
              {baseTermsVersion && (
                // 'order' here is used to display buttons according to design while giving keyboard navigation precedence
                //  to the button above - "See your price" button
                <LargeDialog.Root>
                  <Dialog.Trigger asChild>
                    <Button style={{ order: -1 }} variant="secondary">
                      {t('ADDON_BLOCK_WHATS_INCLUDED_BUTTON_LABEL')}
                    </Button>
                  </Dialog.Trigger>
                  <LargeDialog.Content>
                    <AddonComparisonDialogBody
                      baseTermsVersion={baseTermsVersion}
                      addonTermsVersion="SE_TRAVEL_ADDON-HEDVIG-null-1"
                    />
                  </LargeDialog.Content>
                </LargeDialog.Root>
              )}
            </footer>
          </section>
        </Card.Root>
      </GridLayout.Content>
    </GridLayout.Root>
  )
}
